import type { ReactNode } from 'react'

/**
 * provider 이름을 읽는 소리의 끝 받침. 받침에 따라 갈리는 조사를 이 값으로 고른다.
 *
 * 표기 글자로 가리지 않는다. Google은 구글로 읽어 ㄹ 받침이다. 받침을 있다와 없다 둘로만 나누면
 * 으로와 로에서 틀리므로 ㄹ을 따로 둔다. 새 provider를 열면 이름과 함께 여기에 둔다.
 */
type FinalSound = 'none' | 'rieul' | 'other'

const PROVIDER_FINAL_SOUND: Record<string, FinalSound> = {
  Google: 'rieul',
  네이버: 'none',
}

/** 이름에 을이나 를을 붙인다. 받침이 없으면 를이고 ㄹ을 포함해 받침이 있으면 을이다. */
export function withObjectParticle(name: string): string {
  return `${name}${PROVIDER_FINAL_SOUND[name] === 'none' ? '를' : '을'}`
}

/** 받침 갈래를 정해 둔 이름인지 본다. 사전의 provider 이름이 모두 여기에 들었는지 테스트가 확인한다. */
export function hasFinalSound(name: string): boolean {
  return name in PROVIDER_FINAL_SOUND
}

/**
 * 화면 문구의 정본. 영어와 일본어 사전이 이 모듈에서 뽑은 타입을 만족한다.
 *
 * 키는 기능과 화면 또는 흐름, 요소 셋으로 짓는다. 문구 자체를 키로 쓰지 않는다. 한국어 문구를
 * 다듬을 때마다 키가 바뀌면 세 언어가 함께 어긋난다.
 *
 * 값은 문자열이거나 인자를 받아 돌려주는 함수다. 값을 끼우는 문구는 함수로 둔다. 런타임에
 * 템플릿을 파싱하지 않으므로 인자 이름과 개수를 타입이 강제한다. 문장을 조각으로 이어 붙이지
 * 않고 통째로 두어 어순이 갈리는 언어에서도 문장 하나가 한 자리에서 끝난다.
 */
export const ko = {
  app: {
    layout: {
      settings: '설정',
    },
    update: {
      message: 'mlog가 조금 달라졌어요. 다시 열면 새 화면으로 볼 수 있어요.',
      reopening: '새 화면을 여는 중이에요',
      reopen: '다시 열기',
    },
    notFound: {
      message: '찾는 페이지가 없어요',
      back: '내 기록으로 돌아가기',
    },
    common: {
      retry: '다시 시도',
    },
  },

  auth: {
    gate: {
      opening: '잠시만요, 공간을 여는 중이에요',
      unreachable: '지금은 연결이 어려워요. 잠시 뒤에 다시 열어 주세요',
    },
    login: {
      tagline: '마음을 적어 두는 나만의 자리',
      assurance: '여기 남긴 기록은 나만 볼 수 있어요.',
      establishing: '들어갈 준비를 하고 있어요',
      autoSignUp: '계정이 없으면 이 버튼으로 바로 만들어져요.',
      /**
       * 약관과 처리방침으로 가는 자리 둘이 문장 안에 든다. 조각을 이어 붙이지 않으려고 문장
       * 전체를 이 함수가 갖고 두 자리만 인자로 받는다. 어순이 갈리는 언어는 자기 사전에서
       * 자리를 옮긴다.
       */
      consent: (terms: ReactNode, privacy: ReactNode): ReactNode => (
        <>
          계속하면 {terms}과 {privacy}에 동의하는 것으로 봅니다.
        </>
      ),
      termsLink: '이용약관',
      privacyLink: '개인정보 처리방침',
      /**
       * 시간을 주어로 삼는다. 오래 자리를 비웠다는 말로 사용자를 가리키지 않는다. 만료는
       * 우리가 건 규칙이지 사용자가 어긴 것이 아니다.
       */
      sessionExpired: '시간이 지나 로그인이 끊겼어요. 다시 들어오면 기록은 그대로 있어요.',
    },
    failure: {
      declined: '괜찮아요. 준비되면 다시 눌러 주세요.',
      emailAlreadyRegistered:
        '이 이메일로 만든 공간이 이미 있어요. 처음 쓰던 방법으로 들어온 뒤 설정에서 이 방법을 추가할 수 있어요.',
      emailUnavailable: (provider: string) =>
        `${provider} 계정의 이메일을 확인하지 못해서 아직 공간을 열지 못했어요. ${provider}에서 이메일 확인을 마친 뒤에 다시 눌러 주세요.`,
      unavailable: '로그인을 마치지 못했어요. 한 번 더 눌러 주세요.',
    },
    provider: {
      google: 'Google',
      naver: '네이버',
      continueWith: (provider: string) => `${provider}로 계속하기`,
      leavingTo: (provider: string) => `${provider}로 가는 중이에요`,
    },
    logout: {
      label: '로그아웃',
      pending: '로그아웃하는 중이에요',
      failed: '로그아웃을 마치지 못했어요. 한 번 더 눌러 주세요.',
    },
  },

  /**
   * 가입 직후 한 번 서는 화면. 맞아들이는 인사와 아직 추가하지 않은 provider를 권하는 말이
   * 한 화면에 함께 선다.
   *
   * 추가하겠느냐고 묻지 않는다. 묻는 형태로 두면 대답을 요구하게 되고 건너뛰기가 거절이 된다.
   * 알리고 버튼을 놓아 두는 데서 멈춘다.
   *
   * 막힌 경우의 문구는 설정의 로그인 방법 자리와 같은 것을 쓴다. 두 자리에서 사용자가 할 일이
   * 같아 문구를 갈라 두면 같은 말이 두 벌이 되고 한쪽만 고쳐지는 날이 온다.
   */
  onboarding: {
    greeting: '여기는 오직 당신만의 공간이에요.',
    from: (provider: string) => `지금은 ${provider}로 들어왔어요.`,
    suggest: (provider: string) =>
      `${provider}도 추가해 두면 어느 쪽으로 눌러도 이 공간으로 들어와요.`,
    /** 계속하기가 아니라 추가하기다. 로그인이 아니라 이미 있는 공간에 방법을 더하는 일이다. */
    add: (provider: string) => `${provider} 추가하기`,
    /** 나중에 하겠다는 말로 적지 않는다. 나중을 약속하면 그 약속이 다음 진입에서 되묻는 빌미가 된다. */
    skip: '그냥 들어갈게요',
    later: '설정에서 언제든 추가할 수 있어요.',
  },

  /**
   * 신원이 하나 붙었다는 것을 홈에서 알리는 한 줄.
   *
   * 홈에는 추가가 어떻게 됐는지 보여 주는 것이 없어 아무 말도 하지 않으면 알아채지 못한다.
   * 축하나 권유로 부풀리지 않고 일어난 일만 적는다.
   */
  link: {
    addedNotice: (provider: string) => `${withObjectParticle(provider)} 로그인 방법에 추가했어요.`,
    close: '닫기',
  },

  entry: {
    list: {
      compose: '지금 기록하기',
      loading: '기록을 불러오는 중이에요',
      failed: '기록을 불러오지 못했어요. 잠시 뒤에 다시 열어 주세요',
      empty: '오늘 하루는 어땠나요',
      emptyWelcome: '공간이 열렸어요. 오늘 하루는 어땠나요',
      loadMore: '지난 기록 더 보기',
      loadingMore: '불러오는 중이에요',
    },
    editor: {
      opening: '기록을 여는 중이에요',
      openFailed: '기록을 열지 못했어요',
      moodHint: '지금 기분에 가까운 걸 골라보세요. 안 골라도 괜찮아요',
      placeholder: '지금 떠오르는 대로 적어보세요',
      bodyLabel: '기록 본문',
      saving: '저장하고 있어요',
      saved: '저장했어요',
      saveFailed: '저장하지 못했어요',
    },
    /**
     * 무드 라벨은 화면 언어를 따른다. 값의 정체는 코드와 이모지가 지키고 라벨은 그 값을 지금
     * 화면의 말로 부르는 표기다.
     */
    mood: {
      calm: '차분해요',
      glad: '기뻐요',
      tired: '지쳤어요',
      sad: '가라앉아요',
      anxious: '불안해요',
    },
  },

  settings: {
    screen: {
      title: '설정',
      back: '돌아가기',
    },
    /**
     * 시스템 설정은 화면 언어를 따라 갈리는 유일한 항목이다. 나머지 셋은 어느 화면에서나
     * 자기 이름으로 서서 자리와 순서가 흔들리지 않는다.
     *
     * 브라우저 설정이라 적지 않는다. 홈 화면에서 연 창에는 브라우저가 없다.
     */
    language: {
      label: '언어',
      sheetTitle: '언어',
      sheetHint: '화면에 보이는 말만 바뀌어요. 남긴 기록과 닉네임은 그대로 있어요.',
      system: '시스템 설정',
      saveFailed: '언어 설정을 계정에 남기지 못했어요. 한 번 더 눌러 주세요.',
      close: '닫기',
    },

    /**
     * 화면에 쓰는 말은 닉네임이다. 이름이라 적지 않는다. 이름은 실명을 적는 칸의 말이라
     * provider에서 따라온 값으로 읽힐 여지가 있다.
     *
     * 남에게 보인다는 뜻이 닉네임에 따라 들어오는 것은 안심 문장 한 줄이 받는다.
     *
     * 지어 준 닉네임을 칭찬하지 않는다. 잘 어울린다거나 마음에 드느냐고 묻지 않는다.
     */
    nickname: {
      label: '닉네임',
      sheetTitle: '닉네임',
      sheetHint: '닉네임은 나만 볼 수 있어요. 마음 가는 대로 지어도 괜찮아요.',
      regenerate: '다시 짓기',
      regenerating: '닉네임을 짓는 중이에요',
      regenerateFailed: '닉네임을 짓지 못했어요. 한 번 더 눌러 주세요.',
      save: '저장',
      saving: '저장하는 중이에요',
      saveFailed: '닉네임을 저장하지 못했어요. 한 번 더 눌러 주세요.',
      /** 다음에 할 일을 적지 않는다. 옆에 다시 시도 버튼이 서 있다. */
      loadFailed: '닉네임을 불러오지 못했어요.',
      remaining: (count: number) => `${count}자 남았어요`,
      over: (count: number) => `${count}자만 줄여 주세요`,
      close: '닫기',
    },
    /**
     * 신원이나 연결이 아니라 로그인 방법으로 부른다. 여기서 사용자가 얻는 답은 어느 버튼으로
     * 이 공간에 들어올 수 있느냐이고 나머지는 우리 말이다.
     *
     * 표시를 짧게 두고 그 대신 접근성 이름에 문장을 준다. 목록 항목에 긴 문장을 늘어놓으면
     * 목록이 읽히지 않는다.
     */
    loginMethod: {
      label: '로그인 방법',
      description: '추가한 방법이면 어느 것으로 눌러도 이 공간으로 들어와요.',
      added: '추가됨',
      add: '추가',
      /** 목록에 같은 라벨의 버튼이 여럿이라 소리로 듣는 사람이 어느 것인지 가리게 한다. */
      addName: (provider: string) => `${provider} 추가`,
      linkedName: (provider: string) => `${provider}로 들어올 수 있어요`,
      addedNotice: (provider: string) => `${withObjectParticle(provider)} 추가했어요.`,
      alreadyLinked: (provider: string) =>
        `이 ${provider} 계정은 다른 공간에서 쓰고 있어요. 다른 계정으로 눌러 주세요.`,
      /** 원인을 가리지 못한 자리라 이름을 넣지 않는다. 그 provider로 가서 할 일이 없다. */
      failed: '추가하지 못했어요. 한 번 더 눌러 주세요.',
      loadFailed: '로그인 방법을 불러오지 못했어요.',
    },
    theme: {
      label: '분위기',
      light: '맑은 낮',
      dark: '고요한 밤',
      dusk: '저녁놀',
    },
    legal: {
      terms: '이용약관',
      privacy: '개인정보 처리방침',
    },
  },

  legal: {
    document: {
      back: '돌아가기',
      effectiveDate: (date: string) => `시행일 ${date}`,
    },
  },
}

/**
 * 사전의 모양을 한국어에서 뽑는다. 다른 언어 사전이 이 타입을 만족하므로 키가 빠지거나 인자가
 * 어긋나면 빌드가 막힌다. 빠진 번역을 사람의 규율이 아니라 컴파일이 잡는다.
 *
 * 값을 리터럴로 굳히지 않는다. 굳히면 다른 언어가 같은 글자를 적어야 타입이 맞는다.
 */
export type Messages = typeof ko
